/* Phase C — Admin/Manager surfaces for the Calling App merger.
 *
 * All panels here are permission-gated by the backend. The UI shows/hides
 * the corresponding admin tabs based on the current user's permissions or
 * role. Everything is additive — no existing screen is touched.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Download, Phone, PhoneCall, RotateCcw, ShieldCheck, Trash2, UserCheck, XCircle } from "lucide-react";
import { http, notify } from "../api";
import { Loading, Modal } from "./common";

/* ------------------------------------------------------------------ */
/* SA APPROVAL QUEUE                                                   */
/* ------------------------------------------------------------------ */
export function SaApprovalsTab({ me, teams }) {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () =>
    http.get(`/admin/sa-approvals?status=${status}`)
      .then((r) => setList(r.data.sales_associates))
      .catch((err) => notify.error(err, "Could not load approval queue"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (decision, reason = "", team_id = "", employee_code = "", password = "") => {
    if (!selected) return;
    try {
      await http.post(`/admin/sa-approvals/${selected.employee_id}/decide`, {
        decision, reason, team_id, employee_code, password,
      });
      notify.success(`Sales Associate marked ${decision.replace("_", " ")}`);
      setSelected(null);
      load();
    } catch (err) {
      notify.error(err, "Could not save decision");
    }
  };

  if (list === null) return <Loading>Loading approval queue…</Loading>;

  const tabs = [
    ["pending", "Pending", Clock],
    ["correction_required", "Correction", RotateCcw],
    ["rejected", "Rejected", XCircle],
    ["approved", "Approved", CheckCircle2],
  ];

  return (
    <div className="admin-layout">
      <section className="panel">
        <div className="admin-tabs" style={{ marginBottom: 12 }}>
          {tabs.map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`sa-approval-tab-${id}`}
              className={status === id ? "active" : ""}
              onClick={() => { setStatus(id); setSelected(null); }}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        {list.length === 0 && <div className="empty">No Sales Associates in this bucket.</div>}
        {list.map((sa) => (
          <button
            key={sa.employee_id}
            data-testid={`sa-approval-row-${sa.employee_id}`}
            className={selected?.employee_id === sa.employee_id ? "admin-user selected" : "admin-user"}
            onClick={() => setSelected(sa)}
          >
            {sa.photo_url ? (
              <img className="avatar"
                   src={sa.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${sa.photo_url}` : sa.photo_url}
                   alt={sa.name} style={{ objectFit: "cover" }} />
            ) : (
              <span className="avatar">{(sa.name || "?").slice(0, 2).toUpperCase()}</span>
            )}
            <span>
              <strong>{sa.name || sa.email}</strong>
              <small>
                {sa.email} · {sa.team_name || "Unassigned team"} · {sa.mobile || "no mobile"}
              </small>
            </span>
            <span className={`chip chip-${sa.approval_status}`}>{sa.approval_status.replace("_", " ")}</span>
          </button>
        ))}
      </section>

      <section className="panel permission-panel">
        {selected ? (
          <SaApprovalDetail
            sa={selected}
            teams={teams}
            onDecide={decide}
            canDecide={me?.role === "admin" || ((me?.role !== "sales_manager") && (me?.permissions || []).includes("approve_sales_associates"))}
          />
        ) : (
          <div className="empty">Select a Sales Associate to review their registration.</div>
        )}
      </section>
    </div>
  );
}

function SaApprovalDetail({ sa, teams, onDecide, canDecide }) {
  const [reason, setReason] = useState("");
  const [teamId, setTeamId] = useState(sa.team_id || "");
  const [empCode, setEmpCode] = useState(sa.employee_code || "");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const withHost = (p) => p && p.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p;
  const isSelfRegistered = sa.source === "self_registration";
  const needsPassword = isSelfRegistered;
  const pwdMatches = password === confirmPwd;
  const pwdLongEnough = password.length >= 6;
  const canApprove = canDecide
    && empCode.trim().length > 0
    && (!needsPassword || (pwdMatches && pwdLongEnough));

  return (
    <>
      <p className="eyebrow">REGISTRATION REVIEW</p>
      <h3>{sa.name || "Unnamed"}</h3>
      <p className="muted">
        {sa.email} · {sa.mobile || "no mobile on file"} · Submitted {sa.created_at?.slice(0, 10)}
      </p>

      {!canDecide && (
        <div style={{ marginTop: 12, padding: 12, background: "#f8f9fb", borderRadius: 8 }} data-testid="sa-approval-readonly-summary">
          <div style={{ display: "grid", gap: 6, gridTemplateColumns: "160px 1fr", fontSize: 13 }}>
            <span className="muted">Login Employee ID</span><strong>{sa.employee_code || "—"}</strong>
            <span className="muted">Team</span><strong>{sa.team_name || sa.team_id || "Unassigned"}</strong>
            <span className="muted">Approval status</span><strong>{sa.approval_status || "—"}</strong>
            {sa.rejection_reason && (<><span className="muted">Reason</span><strong>{sa.rejection_reason}</strong></>)}
          </div>
          <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>View-only access. Approval decisions can only be made by an Admin.</p>
        </div>
      )}

      <div className="record-form" style={{ padding: 0, marginTop: 10, display: canDecide ? undefined : "none" }}>
        <div className="form-grid">
          <label>
            <strong style={{ color: !empCode.trim() ? "var(--red)" : undefined }}>
              Login Employee ID *
            </strong>
            <input
              data-testid="sa-approval-empcode-input"
              value={empCode}
              onChange={(e) => setEmpCode(e.target.value.toUpperCase())}
              placeholder="e.g. RX150"
              style={{ textTransform: "uppercase" }}
            />
            <small className="muted" style={{ fontSize: 11 }}>
              You type this by hand. Must be unique. SA will login with it.
            </small>
          </label>
          <label>Assign to team
            <select
              data-testid="sa-approval-team-select"
              value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">Leave unassigned</option>
              {(teams || []).filter((t) => t.team_type === "sales").map((t) => (
                <option key={t.team_id} value={t.team_id}>{t.name}</option>
              ))}
            </select>
          </label>
          {needsPassword && (
            <>
              <label>
                <strong style={{ color: !pwdLongEnough ? "var(--red)" : undefined }}>
                  Initial password *
                </strong>
                <input
                  data-testid="sa-approval-password-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                />
                <small className="muted" style={{ fontSize: 11 }}>
                  SA will be asked to change this on first login.
                </small>
              </label>
              <label>
                <strong style={{ color: !pwdMatches ? "var(--red)" : undefined }}>
                  Confirm password *
                </strong>
                <input
                  data-testid="sa-approval-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Re-type the password"
                />
                {!pwdMatches && confirmPwd.length > 0 && (
                  <small style={{ fontSize: 11, color: "var(--red)" }}>
                    Passwords don't match
                  </small>
                )}
              </label>
            </>
          )}
          <label>Reason (required for reject / correction)
            <input
              data-testid="sa-approval-reason-input"
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. selfie is blurred, please re-upload"
            />
          </label>
        </div>
      </div>

      {sa.photo_url && (
        <div style={{ marginTop: 16 }}>
          <p className="eyebrow">SELFIE</p>
          <img src={withHost(sa.photo_url)} alt="SA selfie"
               data-testid="sa-approval-selfie"
               style={{ maxWidth: 220, borderRadius: 8, border: "1px solid #e6e6e6" }} />
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <p className="eyebrow">KYC DOCUMENTS</p>
        {(!sa.sa_documents || sa.sa_documents.length === 0) ? (
          <p className="muted" data-testid="sa-approval-no-documents">No documents uploaded.</p>
        ) : (
          <ul className="doc-list" data-testid="sa-approval-doc-list">
            {sa.sa_documents.map((d, i) => {
              const href = d.url && d.url.startsWith("/api/")
                ? `${process.env.REACT_APP_BACKEND_URL}${d.url}`
                : d.url;
              const sizeLabel = !d.size
                ? ""
                : d.size < 1024
                  ? `${d.size} B`
                  : `${Math.round(d.size / 1024)} KB`;
              return (
                <li key={i} data-testid={`sa-approval-doc-${d.kind || i}`}>
                  <a data-testid={`sa-approval-doc-link-${d.kind || i}`}
                     href={href} target="_blank" rel="noreferrer"
                     style={{ color: "#2563eb", textDecoration: "underline" }}>
                    {d.type || d.filename || "Document"}
                  </a>
                  <small className="muted"> · {d.content_type || ""}{sizeLabel ? ` · ${sizeLabel}` : ""}</small>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {canDecide && (
        <div className="permission-toolbar" style={{ marginTop: 20 }}>
          <button
            data-testid="sa-approval-approve-button"
            className="primary-button"
            disabled={!canApprove}
            title={!canApprove ? "Enter an Employee ID to approve" : ""}
            onClick={() => onDecide("approved", reason, teamId, empCode.trim().toUpperCase(), password)}
          >
            <CheckCircle2 size={14} /> Approve
          </button>
          <button
            data-testid="sa-approval-correction-button"
            className="outline-button"
            disabled={!reason.trim()}
            onClick={() => onDecide("correction_required", reason)}
            title="Ask the SA to correct info via the Calling App"
          >
            <RotateCcw size={14} /> Ask for correction
          </button>
          <button
            data-testid="sa-approval-reject-button"
            className="outline-button danger-button"
            disabled={!reason.trim()}
            onClick={() => onDecide("rejected", reason)}
          >
            <XCircle size={14} /> Reject
          </button>
        </div>
      )}
    </>
  );
}


/* ------------------------------------------------------------------ */
/* CALLBACKS MONITOR                                                   */
/* ------------------------------------------------------------------ */
export function CallbacksTab() {
  const [range, setRange] = useState("today");
  const [list, setList] = useState(null);

  const load = () =>
    http.get(`/admin/callbacks?range=${range}`)
      .then((r) => setList(r.data.callbacks))
      .catch((err) => notify.error(err, "Could not load callbacks"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  if (list === null) return <Loading>Loading callbacks…</Loading>;

  return (
    <section className="panel">
      <div className="section-title">
        <h3>Callbacks</h3>
        <div className="admin-tabs" style={{ margin: 0 }}>
          {[["today", "Today"], ["overdue", "Overdue"], ["week", "Next 7 days"]].map(([id, label]) => (
            <button key={id} data-testid={`callbacks-range-${id}`}
              className={range === id ? "active" : ""} onClick={() => setRange(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {list.length === 0 ? (
        <div className="empty">No open callbacks in this range.</div>
      ) : (
        <table className="records-table">
          <thead>
            <tr>
              <th>Due</th><th>Customer</th><th>Lead</th><th>Bank</th><th>Assigned to</th><th>Status</th><th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {list.map((f, idx) => {
              const key = f.followup_id || `${f.lead_id}-${f.due_at}-${idx}`;
              return (
                <tr key={key} data-testid={`callback-row-${key}`}>
                  <td>{f.due_at?.slice(0, 16).replace("T", " ")}</td>
                  <td>{f.customer_name || "—"}</td>
                  <td>{f.lead_id}</td>
                  <td>{f.bank_name || "—"}</td>
                  <td>{f.assigned_name || "—"}</td>
                  <td>{f.lead_status || "—"}</td>
                  <td className="muted">{f.notes || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* CAMPAIGNS — full workflow (Deploy 3 rework)                          */
/*   Create with data upload → team assign → auto-distribute → track   */
/* ------------------------------------------------------------------ */
export function CampaignsTab() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    http.get("/admin/campaigns")
      .then((r) => setList(r.data.campaigns))
      .catch((err) => notify.error(err, "Could not load campaigns"));

  useEffect(() => { load(); }, []);

  // Auto-poll while ANY campaign has an in-flight job so the progress bar
  // updates live without the admin having to reload.
  useEffect(() => {
    if (!list) return;
    const running = list.some(
      (c) => c.active_job && ["queued", "running"].includes(c.active_job.status),
    );
    if (!running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [list]);

  const retryJob = async (job_id, e) => {
    e.stopPropagation();
    try {
      await http.post(`/admin/campaigns/jobs/${job_id}/retry`);
      notify.success("Retry queued — the job is running again");
      load();
    } catch (err) { notify.error(err, "Could not retry job"); }
  };

  if (list === null) return <Loading>Loading campaigns…</Loading>;

  return (
    <div className="admin-layout">
      <section className="panel">
        <div className="section-title">
          <h3>Campaigns</h3>
          <button className="primary-button" onClick={() => setCreating(true)} data-testid="campaigns-new-button">
            + New campaign
          </button>
        </div>
        {list.length === 0 && (
          <div className="empty">
            Create your first campaign — upload a CSV/Excel of leads, then assign it to a Sales team.
          </div>
        )}
        {list.map((c) => {
          const job = c.active_job;
          const jobPct = job && job.total_rows
            ? Math.min(100, Math.round((job.processed / job.total_rows) * 100))
            : 0;
          return (
            <button
              key={c.campaign_id}
              data-testid={`campaign-row-${c.campaign_id}`}
              className={selected?.campaign_id === c.campaign_id ? "admin-user selected" : "admin-user"}
              onClick={() => setSelected(c)}
            >
              <span className="avatar">CP</span>
              <span style={{ flex: 1 }}>
                <strong>{c.name}</strong>
                <small>
                  {c.total_leads} leads · {c.team_name || "no team"} · {c.leads_per_sa || 0}/SA
                </small>
                {job && ["queued", "running"].includes(job.status) && (
                  <div
                    data-testid={`campaign-progress-${c.campaign_id}`}
                    style={{ marginTop: 6 }}
                  >
                    <div style={{
                      height: 6, borderRadius: 3, overflow: "hidden",
                      background: "var(--muted-bg, #eee)",
                    }}>
                      <div style={{
                        width: `${jobPct}%`, height: "100%",
                        background: "var(--accent, #4f46e5)",
                        transition: "width 0.3s ease",
                      }} />
                    </div>
                    <small className="muted">
                      Importing {job.processed.toLocaleString()} / {job.total_rows.toLocaleString()} ({jobPct}%)
                    </small>
                  </div>
                )}
                {job && job.status === "failed" && (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                    <small style={{ color: "#dc2626" }}>
                      Import failed · {job.imported}/{job.total_rows} imported
                    </small>
                    {job.retryable && (
                      <button
                        data-testid={`retry-job-${job.job_id}`}
                        className="secondary-button compact"
                        onClick={(e) => retryJob(job.job_id, e)}
                      >Retry</button>
                    )}
                  </div>
                )}
              </span>
              <span className={`chip chip-${c.status}`}>{c.status}</span>
            </button>
          );
        })}
      </section>

      <section className="panel permission-panel">
        {selected ? (
          <CampaignDetail
            key={selected.campaign_id}
            campaignId={selected.campaign_id}
            onChanged={() => { load(); }}
          />
        ) : (
          <div className="empty">Select a campaign to see its data, team, and distribution.</div>
        )}
      </section>

      {creating && (
        <CampaignCreateModal
          onClose={() => setCreating(false)}
          onCreated={(c) => { setCreating(false); load(); setSelected(c); }}
        />
      )}
    </div>
  );
}

function CampaignCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", leads_per_sa: "", team_id: "",
    mode: "manual", campaign_date: new Date().toISOString().slice(0, 10),
    is_available: true,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/config").then((r) => setTeams((r.data.teams || []).filter((t) => t.team_type === "sales"))).catch(() => {});
  }, []);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/admin/campaigns/preview-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (err) { notify.error(err, "Preview failed"); }
    finally { setBusy(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { notify.error("Campaign name is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("leads_per_sa", form.leads_per_sa || 0);
      fd.append("mode", form.mode || "manual");
      fd.append("campaign_date", form.campaign_date || "");
      fd.append("is_available", form.is_available ? "true" : "false");
      if (form.team_id) fd.append("team_id", form.team_id);
      if (file) fd.append("file", file);
      const r = await http.post("/admin/campaigns", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const up = r.data.upload || {};
      if (up.job_id) {
        // Large upload — poll the background job every 2s until done/failed.
        notify.success(
          `Import queued · ${up.total_rows.toLocaleString()} rows processing in background…`,
        );
        onCreated(r.data.campaign);
        pollCampaignJob(up.job_id, r.data.campaign);
      } else {
        notify.success(
          `Campaign created · ${r.data.campaign.total_leads} imported · ${up.duplicates || 0} duplicates · ${up.invalid || 0} invalid`,
        );
        onCreated(r.data.campaign);
      }
    } catch (err) { notify.error(err, "Could not create campaign"); }
    finally { setBusy(false); }
  };

  async function pollCampaignJob(jobId, campaign) {
    // Poll up to ~15 min at 2s intervals.
    for (let i = 0; i < 450; i++) {
      try {
        const j = await http.get(`/admin/campaigns/jobs/${jobId}`);
        const d = j.data;
        if (d.status === "done") {
          notify.success(
            `${campaign.name} import complete · ${d.imported} imported · ${d.duplicates} duplicates · ${d.invalid} invalid`,
          );
          return;
        }
        if (d.status === "failed") {
          notify.error(new Error(d.error || "Import failed"), "Campaign import failed");
          return;
        }
      } catch (e) { /* transient — keep polling */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
    notify.error(new Error("Timed out after 15 minutes"), "Campaign import still running");
  }

  return (
    <Modal title="New campaign" onClose={onClose}>
      <form className="record-form" onSubmit={create}>
        <div className="form-grid">
          <label>Campaign name
            <input required data-testid="campaign-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. DATA DATA" />
          </label>
          <label>Description
            <input data-testid="campaign-description-input" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Personal Loan leads Sep 2026" />
          </label>
          <label>Team (optional now, can assign later)
            <select data-testid="campaign-team-select" value={form.team_id}
              onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
              <option value="">Assign later</option>
              {teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
            </select>
          </label>
          <label>Leads per Sales Associate
            <input type="number" min="0" data-testid="campaign-leads-per-sa-input"
              value={form.leads_per_sa}
              onChange={(e) => setForm({ ...form, leads_per_sa: e.target.value })}
              placeholder="e.g. 50" />
          </label>
          <label>Mode
            <select data-testid="campaign-mode-select" value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="manual">Manual — admin distributes</option>
              <option value="automatic">Automatic — 10-lead rolling buffer + smart balancing</option>
            </select>
          </label>
          <label>Campaign date
            <input type="date" data-testid="campaign-date-input"
              value={form.campaign_date}
              onChange={(e) => setForm({ ...form, campaign_date: e.target.value })} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" data-testid="campaign-available-checkbox"
              checked={form.is_available}
              onChange={(e) => setForm({ ...form, is_available: e.target.checked })} />
            Available to SAs immediately
          </label>
        </div>

        <div style={{ marginTop: 10, padding: 12, border: "1px dashed #ccc", borderRadius: 8 }}>
          <p className="eyebrow">CUSTOMER DATA (CSV / EXCEL)</p>
          <input type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            data-testid="campaign-file-input" />
          <button type="button" className="outline-button" disabled={!file || busy}
            onClick={doPreview} style={{ marginLeft: 12 }} data-testid="campaign-preview-button">
            {busy ? "Reading…" : "Preview"}
          </button>
          <button
            type="button"
            className="outline-button"
            style={{ marginLeft: 8 }}
            data-testid="campaign-download-template"
            onClick={() => {
              // Header row: every column the importer recognises + a handful
              // of free-form "extras" so admins see they can attach anything.
              const headers = [
                "external_id", "customer_name", "mobile", "email", "pan",
                "city", "state", "pincode",
                "employment_type", "company", "designation",
                "monthly_income", "existing_emi", "existing_pos",
                "loan_type", "loan_amount", "loan_purpose",
                "bank_name", "product_name", "roi", "tenure_months",
                // ---- free-form examples (rides along under "Other Information") ----
                "lead_source", "utm_campaign", "cibil_score",
                "preferred_language", "notes",
              ];
              const sample1 = [
                "EXT-1001", "Amit Verma", "9990001101", "amit@example.com", "ABCDE1234F",
                "Pune", "MH", "411001",
                "Salaried", "Infosys", "Manager",
                "85000", "12500", "220000",
                "Personal Loan", "500000", "Home renovation",
                "HDFC BANK", "PL – Salaried", "10.75", "60",
                "IVR", "aug_fest", "742",
                "Marathi", "Prefers calls after 6 PM",
              ];
              const sample2 = [
                "EXT-1002", "Priya Nair", "9990001102", "priya@example.com", "ABCDE5678K",
                "Bengaluru", "KA", "560001",
                "Salaried", "Wipro", "Team Lead",
                "150000", "0", "0",
                "Home Loan", "3500000", "Purchase",
                "ICICI BANK", "HL – Prime", "8.5", "240",
                "Website", "instagram_reels", "798",
                "English", "First-time buyer",
              ];
              const csv = [headers, sample1, sample2]
                .map((row) => row
                  .map((c) => /[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)
                  .join(","))
                .join("\n");
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "campaign-customers-template.csv";
              a.click();
              URL.revokeObjectURL(url);
              notify.success("Sample template downloaded");
            }}
          >
            <Download size={13} /> Download sample template
          </button>
          <p className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
            Only <strong>customer_name</strong> and <strong>mobile</strong> are required.
            Any additional columns (e.g. <em>lead_source, cibil_score, notes…</em>) travel
            with the customer and appear under "Other Information" for the SA.
          </p>
          {preview && (
            <div style={{ marginTop: 8 }} data-testid="campaign-preview-summary">
              <strong>{preview.total_rows}</strong> rows found · <strong>{preview.valid}</strong> valid ·{" "}
              <strong>{preview.duplicates}</strong> duplicates · <strong>{preview.invalid}</strong> invalid
              {preview.warnings?.length > 0 && <span className="muted"> · {preview.warnings.length} warnings</span>}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} data-testid="campaign-submit-button">
            {busy ? "Creating…" : (file ? "Create + Import" : "Create campaign")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CampaignDetail({ campaignId, onChanged }) {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ team_id: "", leads_per_sa: "" });
  const [reassignFor, setReassignFor] = useState(null);   // {sa_id, sa_name} of source SA

  const load = () => {
    http.get(`/admin/campaigns/${campaignId}`).then((r) => {
      setData(r.data);
      setForm({ team_id: r.data.team_id || "", leads_per_sa: r.data.leads_per_sa || "" });
    }).catch((err) => notify.error(err, "Could not load campaign"));
    http.get(`/admin/campaigns/${campaignId}/analytics`).then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    http.get("/admin/config").then((r) => setTeams((r.data.teams || []).filter((t) => t.team_type === "sales"))).catch(() => {});
    /* eslint-disable-next-line */
  }, [campaignId]);

  if (!data) return <Loading>Loading campaign…</Loading>;

  const saveTeam = async () => {
    if (!form.team_id) { notify.error("Pick a team first"); return; }
    setBusy(true);
    try {
      await http.post(`/admin/campaigns/${campaignId}/assign-team`, {
        team_id: form.team_id, leads_per_sa: Number(form.leads_per_sa) || 0,
      });
      notify.success("Team assigned");
      load(); onChanged();
    } catch (err) { notify.error(err, "Could not save team"); }
    finally { setBusy(false); }
  };

  const distribute = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/admin/campaigns/${campaignId}/distribute`);
      const msg = r.data.assigned > 0
        ? `Assigned ${r.data.assigned} leads across ${r.data.sa_count} Sales Associates`
        : `Nothing to distribute — ${r.data.reason}`;
      notify.success(msg);
      load(); onChanged();
    } catch (err) { notify.error(err, "Distribute failed"); }
    finally { setBusy(false); }
  };

  const autoBalance = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/admin/campaigns/${campaignId}/auto-balance`);
      notify.success(r.data.summary || `Auto-balanced ${r.data.assigned || 0} leads`);
      load(); onChanged();
    } catch (err) { notify.error(err, "Auto-balance failed"); }
    finally { setBusy(false); }
  };

  const saveSettings = async (patch) => {
    try {
      await http.post(`/admin/campaigns/${campaignId}/settings`, patch);
      notify.success("Campaign updated");
      load(); onChanged();
    } catch (err) { notify.error(err, "Could not update campaign"); }
  };

  const setStatus = async (status) => {
    if (status === "stopped" && !window.confirm("Stop this campaign? Unassigned leads will remain but no new distribution will happen.")) return;
    setBusy(true);
    try {
      await http.post(`/admin/campaigns/${campaignId}/status`, { status });
      notify.success(`Campaign ${status}`);
      load(); onChanged();
    } catch (err) { notify.error(err, "Status change failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this campaign? Existing lead records are NOT deleted.")) return;
    try {
      const r = await http.delete(`/admin/campaigns/${campaignId}`);
      notify.success(r.data.soft_stopped ? "Campaign stopped" : "Deleted");
      onChanged();
    } catch (err) { notify.error(err, "Delete failed"); }
  };

  return (
    <>
      <p className="eyebrow">CAMPAIGN</p>
      <h3>{data.name}</h3>
      <p className="muted">{data.description || "—"}</p>
      <p style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span className={`chip chip-${data.status}`}>{data.status}</span>
        <span className="chip" data-testid={`campaign-mode-chip-${data.campaign_id}`}>
          {data.mode === "automatic" ? "Automatic · 10-lead buffer" : "Manual"}
        </span>
        <span className="chip">
          {data.is_available ? "Available" : "Unavailable"}
        </span>
        {data.campaign_date && (
          <span className="chip mono">{data.campaign_date}</span>
        )}
        <span className="chip mono">{data.campaign_id}</span>
      </p>

      <div className="form-grid" style={{ marginTop: 10 }}>
        <label>Mode
          <select value={data.mode || "manual"}
            data-testid="campaign-detail-mode-select"
            onChange={(e) => saveSettings({ mode: e.target.value })}>
            <option value="manual">Manual</option>
            <option value="automatic">Automatic</option>
          </select>
        </label>
        <label>Campaign date
          <input type="date" value={data.campaign_date || ""}
            data-testid="campaign-detail-date-input"
            onChange={(e) => saveSettings({ campaign_date: e.target.value })} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={!!data.is_available}
            data-testid="campaign-detail-available-checkbox"
            onChange={(e) => saveSettings({ is_available: e.target.checked })} />
          Available to Sales Associates
        </label>
      </div>

      <div className="kpi-row" style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", margin: "10px 0" }}>
        <Kpi label="Total leads" value={data.total_leads} />
        <Kpi label="Unassigned" value={data.unassigned_count} />
        <Kpi label="Assigned" value={data.assigned_count} />
        <Kpi label="Completed" value={data.completed_count} />
        <Kpi label="Team" value={data.team_name || "—"} small />
        <Kpi label="Leads/SA" value={data.leads_per_sa || 0} />
      </div>

      <div className="record-form" style={{ padding: 0 }}>
        <div className="form-grid">
          <label>Assign team
            <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="campaign-detail-team-select">
              <option value="">— pick a team —</option>
              {teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
            </select>
          </label>
          <label>Leads per Sales Associate
            <input type="number" min="0" value={form.leads_per_sa}
              onChange={(e) => setForm({ ...form, leads_per_sa: e.target.value })}
              data-testid="campaign-detail-per-sa-input" />
          </label>
        </div>
        <div className="permission-toolbar" style={{ marginTop: 8, flexWrap: "wrap", gap: 8 }}>
          <button className="outline-button" disabled={busy} onClick={saveTeam} data-testid="campaign-save-team-button">Save team & cap</button>
          <button className="primary-button" disabled={busy || !data.team_id || !data.leads_per_sa}
            onClick={distribute} data-testid="campaign-distribute-button">
            Distribute now
          </button>
          {data.mode === "automatic" && (
            <button className="outline-button" disabled={busy || !data.team_id}
              onClick={autoBalance} data-testid="campaign-auto-balance-button">
              Auto-balance now
            </button>
          )}
          {data.status === "draft" || data.status === "paused" ? (
            <button className="outline-button" disabled={busy} onClick={() => setStatus("active")}>Start</button>
          ) : null}
          {data.status === "active" && (
            <button className="outline-button" disabled={busy} onClick={() => setStatus("paused")}>Pause</button>
          )}
          {data.status !== "stopped" && data.status !== "completed" && (
            <button className="outline-button danger-button" disabled={busy} onClick={() => setStatus("stopped")}>Stop</button>
          )}
          <span style={{ flex: 1 }} />
          <button className="icon-button" onClick={remove} title="Delete campaign"><Trash2 size={13} /></button>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <p className="eyebrow">SALES ASSOCIATE DISTRIBUTION</p>
        {data.distribution?.length ? (
          <table className="records-table">
            <thead><tr><th>Sales Associate</th><th>Assigned</th><th>Completed</th><th>Remaining</th><th></th></tr></thead>
            <tbody>
              {data.distribution.map((s) => (
                <tr key={s.sa_employee_id} data-testid={`campaign-dist-row-${s.sa_employee_id}`}>
                  <td>{s.sa_name || s.sa_employee_id}</td>
                  <td>{s.assigned + s.completed}</td>
                  <td>{s.completed}</td>
                  <td>{s.assigned}</td>
                  <td>
                    <button
                      className="outline-button compact"
                      data-testid={`reassign-open-${s.sa_employee_id}`}
                      onClick={() => setReassignFor({ sa_id: s.sa_employee_id, sa_name: s.sa_name })}
                    >Reassign</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">No leads distributed yet. Assign a team and click Distribute.</div>
        )}
      </div>

      {reassignFor && (
        <ReassignModal
          campaignId={campaignId}
          teamId={data.team_id}
          source={reassignFor}
          onClose={() => setReassignFor(null)}
          onDone={() => { setReassignFor(null); load(); onChanged(); }}
        />
      )}

      {analytics && (
        <div style={{ marginTop: 18 }}>
          <p className="eyebrow">CAMPAIGN ANALYTICS</p>
          <div className="kpi-row" style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
            <Kpi label="Calls" value={analytics.calls} />
            <Kpi label="Callbacks" value={analytics.callbacks} />
            <Kpi label="Follow-ups" value={analytics.followups} />
            <Kpi label="Applications" value={analytics.applications} />
            <Kpi label="Approved" value={analytics.approved} />
            <Kpi label="Disbursed" value={analytics.disbursed} />
          </div>
        </div>
      )}
    </>
  );
}

function ReassignModal({ campaignId, teamId, source, onClose, onDone }) {
  const [tab, setTab] = useState("assigned");    // "assigned" | "dispositions"
  const [buckets, setBuckets] = useState(null);  // preview counts
  const [teamSAs, setTeamSAs] = useState([]);    // eligible targets
  const [toSaId, setToSaId] = useState("");
  const [count, setCount] = useState("");        // 0 = all
  const [picked, setPicked] = useState({});      // {dispoName: true}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!source?.sa_id) return;
    http.get(`/admin/campaigns/${campaignId}/reassign-buckets?from_sa_id=${source.sa_id}`)
      .then((r) => setBuckets(r.data))
      .catch((e) => notify.error(e, "Could not load reassign preview"));
  }, [campaignId, source]);

  useEffect(() => {
    if (!teamId) return;
    http.get("/sa/performance-scope")
      .then((r) => {
        const sas = (r.data.sales_associates || [])
          .filter((s) => s.team_id === teamId && s.id !== source.sa_id);
        setTeamSAs(sas);
      })
      .catch(() => setTeamSAs([]));
  }, [teamId, source]);

  const pickedList = Object.keys(picked).filter((k) => picked[k]);
  const pickedTotal = (buckets?.dispositions || [])
    .filter((d) => picked[d.name])
    .reduce((a, b) => a + b.count, 0);

  const submit = async () => {
    if (!toSaId) { notify.error(null, "Pick a destination Sales Associate"); return; }
    setBusy(true);
    try {
      const body = { from_sa_id: source.sa_id, to_sa_id: toSaId };
      if (tab === "dispositions") {
        if (!pickedList.length) { notify.error(null, "Pick at least one disposition"); setBusy(false); return; }
        body.dispositions = pickedList;
      } else {
        // Assigned Data mode — unworked only
        body.count = Number(count) || 0;
      }
      const r = await http.post(`/admin/campaigns/${campaignId}/reassign`, body);
      notify.success(`Reassigned ${r.data.reassigned} record${r.data.reassigned === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { notify.error(e, "Reassign failed"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }} onClick={onClose}>
      <div
        style={{ background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reassign-modal"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Reassign from <span className="mono">{source.sa_name || source.sa_id}</span></h3>
          <button className="outline-button compact" onClick={onClose} data-testid="reassign-close">Close</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            className={tab === "assigned" ? "primary-button compact" : "outline-button compact"}
            onClick={() => setTab("assigned")}
            data-testid="reassign-tab-assigned"
          >Assigned Data{buckets && ` (${buckets.assigned_unworked})`}</button>
          <button
            className={tab === "dispositions" ? "primary-button compact" : "outline-button compact"}
            onClick={() => setTab("dispositions")}
            data-testid="reassign-tab-dispositions"
          >Disposition Data{buckets && ` (${(buckets.dispositions || []).reduce((a, b) => a + b.count, 0)})`}</button>
        </div>

        {!buckets ? <Loading>Loading preview…</Loading> : (
          <div style={{ marginTop: 12 }}>
            {tab === "assigned" ? (
              <div>
                <p className="muted" style={{ marginTop: 0 }}>
                  Remaining uncalled campaign customers currently held by this SA.
                  Called / worked / locked rows are excluded.
                </p>
                <div style={{ padding: 12, background: "#f8f9fb", borderRadius: 8, marginBottom: 10 }}>
                  <strong data-testid="reassign-assigned-count">{buckets.assigned_unworked}</strong> uncalled records ready to transfer.
                  {buckets.locked > 0 && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {buckets.locked} locked record{buckets.locked === 1 ? "" : "s"} (Login / Approved / Disbursed / Sent to Login / Created Lead) will NEVER be moved.
                    </div>
                  )}
                </div>
                <label>
                  Transfer count (leave blank = all)
                  <input
                    type="number" min="0"
                    value={count}
                    placeholder={`0 = all ${buckets.assigned_unworked}`}
                    onChange={(e) => setCount(e.target.value)}
                    data-testid="reassign-count-input"
                  />
                </label>
              </div>
            ) : (
              <div>
                <p className="muted" style={{ marginTop: 0 }}>
                  Pick one or more dispositions to transfer. Locked dispositions
                  (Login / Approved / Disbursed / Sent to Login / Created Lead)
                  are never listed and cannot be reassigned.
                </p>
                {!buckets.dispositions.length ? (
                  <div className="empty">No worked dispositions to reassign.</div>
                ) : (
                  <div style={{ display: "grid", gap: 6 }}>
                    {buckets.dispositions.map((d) => (
                      <label key={d.name}
                             style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, border: "1px solid #eee", borderRadius: 6 }}
                             data-testid={`reassign-dispo-${d.name}`}>
                        <input
                          type="checkbox"
                          checked={!!picked[d.name]}
                          onChange={(e) => setPicked((p) => ({ ...p, [d.name]: e.target.checked }))}
                        />
                        <span style={{ flex: 1 }}>{d.name}</span>
                        <strong>{d.count}</strong>
                      </label>
                    ))}
                    <div style={{ padding: 8, background: "#f8f9fb", borderRadius: 6 }}>
                      Selected: <strong data-testid="reassign-picked-total">{pickedTotal}</strong> record{pickedTotal === 1 ? "" : "s"}
                    </div>
                  </div>
                )}
              </div>
            )}

            <label style={{ marginTop: 12 }}>
              Destination Sales Associate
              <select value={toSaId} onChange={(e) => setToSaId(e.target.value)} data-testid="reassign-to-sa">
                <option value="">Pick a Sales Associate…</option>
                {teamSAs.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.employee_code ? ` · ${s.employee_code}` : ""}</option>
                ))}
              </select>
            </label>

            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button className="outline-button" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="primary-button"
                onClick={submit}
                disabled={busy || !toSaId}
                data-testid="reassign-submit"
              >Reassign</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function Kpi({ label, value, small }) {  return (
    <div style={{ padding: 10, borderRadius: 8, border: "1px solid #eee" }}>
      <p className="eyebrow" style={{ margin: 0 }}>{label}</p>
      <strong style={{ fontSize: small ? 14 : 20 }}>{value}</strong>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* CALLING SCRIPTS                                                     */
/* ------------------------------------------------------------------ */
export function ScriptsTab() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    http.get("/admin/scripts")
      .then((r) => setList(r.data.scripts))
      .catch((err) => notify.error(err, "Could not load scripts"));

  useEffect(() => { load(); }, []);

  const del = async (s) => {
    if (!window.confirm(`Delete script ${s.title}?`)) return;
    try {
      await http.delete(`/admin/scripts/${s.script_id}`);
      notify.success("Deleted");
      setSelected(null);
      load();
    } catch (err) { notify.error(err, "Could not delete"); }
  };

  if (list === null) return <Loading>Loading scripts…</Loading>;

  return (
    <div className="admin-layout">
      <section className="panel">
        <div className="section-title">
          <h3>Calling scripts</h3>
          <button className="outline-button" onClick={() => setCreating(true)} data-testid="scripts-new-button">
            + New script
          </button>
        </div>
        {list.length === 0 && <div className="empty">No scripts yet.</div>}
        {list.map((s) => (
          <button
            key={s.script_id}
            data-testid={`script-row-${s.script_id}`}
            className={selected?.script_id === s.script_id ? "admin-user selected" : "admin-user"}
            onClick={() => setSelected(s)}
          >
            <span className="avatar">SC</span>
            <span>
              <strong>{s.title}</strong>
              <small>{s.loan_type || "All products"} · {s.active ? "Active" : "Inactive"}</small>
            </span>
          </button>
        ))}
      </section>
      <section className="panel permission-panel">
        {selected ? (
          <ScriptDetail
            key={selected.script_id}
            script={selected}
            onSaved={() => { load(); }}
            onDeleted={() => del(selected)}
          />
        ) : (
          <div className="empty">Select or create a script.</div>
        )}
      </section>
      {creating && <ScriptModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ScriptDetail({ script, onSaved, onDeleted }) {
  const [form, setForm] = useState({ ...script });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({ ...script }); }, [script]);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/scripts/${script.script_id}`, {
        title: form.title, loan_type: form.loan_type || "",
        body: form.body || "", active: !!form.active,
      });
      notify.success("Saved");
      onSaved();
    } catch (err) { notify.error(err, "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <p className="eyebrow">SCRIPT</p>
      <h3>{form.title}</h3>
      <div className="record-form" style={{ padding: 0 }}>
        <div className="form-grid">
          <label>Title<input data-testid="script-title-input" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label>Loan type<input data-testid="script-loan-type-input" value={form.loan_type || ""} onChange={(e) => setForm({ ...form, loan_type: e.target.value })} placeholder="e.g. Personal Loan" /></label>
          <label>Active
            <select data-testid="script-active-select" value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
              <option value="yes">Yes</option><option value="no">No</option>
            </select>
          </label>
        </div>
        <label>Script body
          <textarea
            data-testid="script-body-input"
            rows={12} value={form.body || ""}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Hi, I'm calling from Rupee Xpert regarding your personal loan enquiry…"
          />
        </label>
        <div className="form-actions">
          <button className="outline-button danger-button" onClick={onDeleted}>Delete</button>
          <button className="primary-button" onClick={save} disabled={busy} data-testid="script-save-button">
            {busy ? "Saving…" : "Save script"}
          </button>
        </div>
      </div>
    </>
  );
}

function ScriptModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", loan_type: "", body: "", active: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      notify.error("Title and script body are required");
      return;
    }
    setBusy(true);
    try {
      await http.post("/admin/scripts", form);
      notify.success("Script created");
      onSaved();
    } catch (err) { notify.error(err, "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New calling script" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <div className="form-grid">
          <label>Title<input required data-testid="script-modal-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label>Loan type<input data-testid="script-modal-loan-type-input" value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })} placeholder="Optional" /></label>
        </div>
        <label>Script body
          <textarea required rows={10} data-testid="script-modal-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </label>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} data-testid="script-modal-submit-button">
            {busy ? "Saving…" : "Create script"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ------------------------------------------------------------------ */
/* SA PERFORMANCE (Manager dashboard card + full grid)                 */
/* ------------------------------------------------------------------ */
export function SaPerformancePanel({ days = 30, compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    http.get(`/reports/sa-performance?days=${days}`)
      .then((r) => setData(r.data))
      .catch((err) => notify.error(err, "Could not load SA performance"));
  }, [days]);

  if (!data) return <div className="panel"><Loading>Loading performance…</Loading></div>;
  const rows = compact ? data.sales_associates.slice(0, 6) : data.sales_associates;

  return (
    <section className="panel" data-testid="sa-performance-panel">
      <div className="section-title">
        <h3><PhoneCall size={16} /> SA performance · last {data.days} days</h3>
        <span className="scope-chip"><ShieldCheck size={14} /> {data.sales_associates.length} SAs</span>
      </div>
      {rows.length === 0 ? (
        <div className="empty">No approved Sales Associates yet.</div>
      ) : (
        <table className="records-table">
          <thead><tr>
            <th>Sales Associate</th><th>Calls</th><th>Connected</th><th>Connect %</th><th>Callbacks</th><th>Avg talk</th><th>Last seen</th>
          </tr></thead>
          <tbody>
            {rows.map((sa) => (
              <tr key={sa.employee_id} data-testid={`sa-performance-row-${sa.employee_id}`}>
                <td>
                  <strong>{sa.name}</strong>
                  <small className="muted"> · {sa.team_name}</small>
                </td>
                <td>{sa.total_calls}</td>
                <td>{sa.connected_calls}</td>
                <td>{sa.connect_rate_pct}%</td>
                <td>{sa.callbacks_scheduled}</td>
                <td>{formatDuration(sa.avg_talk_seconds)}</td>
                <td>
                  {sa.last_seen_status ? (
                    <span className={`chip chip-${sa.last_seen_status}`}>{sa.last_seen_status}</span>
                  ) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* CALLING HISTORY (Lead Detail tab)                                    */
/* ------------------------------------------------------------------ */
export function CallingHistoryPanel({ leadId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!leadId) return;
    http.get(`/leads/${leadId}/calling-history`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) setData({ dispositions: [], events: [], denied: true });
        else notify.error(err, "Could not load calling history");
      });
  }, [leadId]);
  if (!data) return <Loading>Loading calling history…</Loading>;
  if (data.denied) return <div className="empty">You do not have permission to view calling activity.</div>;

  const items = [...(data.dispositions || []).map((d) => ({ ...d, kind: "disposition" })),
                 ...(data.events || []).map((e) => ({ ...e, kind: "event" }))]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  if (items.length === 0) return <div className="empty">No calls logged for this lead yet.</div>;

  return (
    <ul className="timeline" data-testid="calling-history-list">
      {items.map((x, i) => (
        <li key={i} className="timeline-item">
          <span className="timeline-icon"><Phone size={14} /></span>
          <div>
            {x.kind === "disposition" ? (
              <>
                <strong>{x.disposition || "Disposition"}</strong>
                {x.call_outcome ? <span className="muted"> · {x.call_outcome}</span> : null}
                <div className="muted">
                  {x.sa_name || "Sales Associate"} ·
                  {" "}{formatDuration(x.duration_seconds || 0)} ·
                  {" "}{(x.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
                {x.notes && <div style={{ marginTop: 4 }}>{x.notes}</div>}
                {x.callback_at && (
                  <div className="muted">Callback scheduled: {x.callback_at.slice(0, 16).replace("T", " ")}</div>
                )}
              </>
            ) : (
              <>
                <strong>{(x.event || "").replace("webhook.", "")}</strong>
                <div className="muted">{(x.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}


/* ------------------------------------------------------------------ */
function formatDuration(seconds) {
  seconds = Number(seconds) || 0;
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}
